import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { getSkuBrands } from "../../../services/sku";
import {
  getCarBrands,
  getCarModels,
  getCarTransmissionsAndCCs,
  getCarYears,
} from "../../../services/car";
import {
  skuBrandsMapper,
  carBrandsMapper,
  carModelsMapper,
  carTransmissionAndCCMapper,
  carYearListMapper,
} from "../../../helpers/dataMapper";
import { SKUS_QUERY_STRING } from "../../../constants/skus";
import SkusFilterDropdown from "./SkusFilterDropdown/SkusFilterDropdown";
import classes from "./SkusFilters.module.css";

const SkusFilters = (props) => {
  const { className } = props;
  const [skuBrands, setSkusBrands] = useState([]);
  const [isSkuBrandsLoading, setIsSkuBrandsLoading] = useState(true);
  const [skuBrandsLoadingError, setSkuBrandsLoadingError] = useState("");
  const [carBrands, setCarBrands] = useState([]);
  const [isCarBrandsLoading, setIsCarBrandsLoading] = useState(true);
  const [carBrandsLoadingError, setCarBrandsLoadingError] = useState("");
  const [carModels, setCarModals] = useState([]);
  const [isCarModelsLoading, setIsCarModelsLoading] = useState(false);
  const [carModelsLoadingError, setCarModelsLoadingError] = useState("");
  const [carTransmissionsAndCCs, setCarTransmissionsAndCCs] = useState([]);
  const [isCarTransmissionsAndCCsLoading, setIsCarTransmissionsAndCCsLoading] =
    useState(false);
  const [carTransmissionsAndCCsError, setCarTransmissionsAndCCsError] =
    useState("");
  const [carYearList, setCarYearList] = useState([]);
  const [isCarYearListLoading, setIsCarYearListLoading] = useState(false);
  const [carYearListLoadingError, setCarYearListLoadingError] = useState("");
  const [searchParams] = useSearchParams();
  const categoryId = Number(searchParams.get(SKUS_QUERY_STRING.CATEGORY_ID));
  const subcategoryId = Number(
    searchParams.get(SKUS_QUERY_STRING.SUB_CATEGORY_ID)
  );
  const skuBrandsQueryString = searchParams
    .get(SKUS_QUERY_STRING.SKU_BRANDS)
    ?.trim();
  const carBrandsQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_BRANDS)
    ?.trim();
  const carModelsQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_MODELS)
    ?.trim();
  const carTransmissionAndCCQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_TRANSMISSION_CC)
    ?.trim();
  const carYearListQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_YEAR_LIST)
    ?.trim();

  useEffect(() => {
    const fetchSkuBrands = async () => {
      try {
        setIsSkuBrandsLoading(true);
        setSkuBrandsLoadingError("");
        const response = await getSkuBrands(categoryId, subcategoryId);
        const {
          data: { skuBrandsList },
        } = response;
        setSkusBrands(skuBrandsMapper(skuBrandsList));
      } catch (err) {
        setSkuBrandsLoadingError("عفوا لا يوجد انواع لعرضها !");
      } finally {
        setIsSkuBrandsLoading(false);
      }
    };

    fetchSkuBrands();
  }, [categoryId, subcategoryId]);

  useEffect(() => {
    const fetchCarBrands = async () => {
      try {
        setIsCarBrandsLoading(true);
        setCarBrandsLoadingError("");
        const response = await getCarBrands();
        const {
          data: { carBrandsList },
        } = response;
        setCarBrands(carBrandsMapper(carBrandsList));
      } catch (err) {
        setSkuBrandsLoadingError("عفوا لا يوجد ماركات لعرضها !");
      } finally {
        setIsCarBrandsLoading(false);
      }
    };

    fetchCarBrands();
  }, []);

  useEffect(() => {
    const fetchCarModels = async () => {
      try {
        setIsCarModelsLoading(true);
        setCarModelsLoadingError("");
        const response = await getCarModels(carBrandsQueryString);
        const {
          data: { carModelList },
        } = response;
        setCarModals(carModelsMapper(carModelList));
      } catch (err) {
        setCarModelsLoadingError("عفوا لا يوجد موديلات لعرضها !");
      } finally {
        setIsCarModelsLoading(false);
      }
    };

    if (carBrandsQueryString) {
      fetchCarModels();
    }
  }, [carBrandsQueryString]);

  useEffect(() => {
    const fetchCarTransmissionsAndCCs = async () => {
      try {
        setIsCarTransmissionsAndCCsLoading(true);
        setCarTransmissionsAndCCsError("");
        const response = await getCarTransmissionsAndCCs(carModelsQueryString);
        const {
          data: { result },
        } = response;
        setCarTransmissionsAndCCs(carTransmissionAndCCMapper(result));
      } catch (err) {
        setCarTransmissionsAndCCsError("عفواً لا يوجد أختيارات لسعة الموتور !");
      } finally {
        setIsCarTransmissionsAndCCsLoading(false);
      }
    };

    if (carModelsQueryString) {
      fetchCarTransmissionsAndCCs();
    }
  }, [carModelsQueryString]);

  useEffect(() => {
    const fetchCarYears = async () => {
      try {
        setIsCarYearListLoading(true);
        setCarYearListLoadingError("");
        const response = await getCarYears(carModelsQueryString);
        const {
          data: { result },
        } = response;
        setCarYearList(carYearListMapper(result));
      } catch (err) {
        setCarYearListLoadingError("عفواً لا يوجد سنين لعرضها !");
      } finally {
        setIsCarYearListLoading(false);
      }
    };

    if (carModelsQueryString) {
      fetchCarYears();
    }
  }, [carModelsQueryString]);

  const getDropdownText = (queryString, list = []) => {
    let text = "";
    if (queryString) {
      if (list.length !== 0) {
        const filteredIds = queryString
          ?.split(",")
          ?.map((value) => Number(value));
        list.forEach((item) => {
          if (filteredIds.includes(item.id)) {
            text = text + `${item.name}, `;
          }
        });
      } else {
        text = queryString;
      }
    }

    return text || "الكل";
  };

  return (
    <ul className={classNames(classes["skus-filters"], className)}>
      <SkusFilterDropdown
        label="النوع"
        dropdownText={getDropdownText(skuBrandsQueryString, skuBrands)}
        menu={skuBrands}
        isMenuLoading={isSkuBrandsLoading}
        menuLoadingError={
          skuBrandsLoadingError || skuBrands.length === 0
            ? "عفوا لا يوجد انواع لعرضها !"
            : ""
        }
        queryStringName={SKUS_QUERY_STRING.SKU_BRANDS}
      />
      <SkusFilterDropdown
        label="ماركة السيارة"
        dropdownText={getDropdownText(carBrandsQueryString, carBrands)}
        menu={carBrands}
        isMenuLoading={isCarBrandsLoading}
        menuLoadingError={
          carBrandsLoadingError || carBrands.length === 0
            ? "عفوا لا يوجد ماركات لعرضها !"
            : ""
        }
        queryStringName={SKUS_QUERY_STRING.CAR_BRANDS}
      />
      <SkusFilterDropdown
        label="موديل السيارة"
        dropdownText={getDropdownText(carModelsQueryString, carModels)}
        menu={carModels}
        isDisabled={!carBrandsQueryString}
        isMenuLoading={isCarModelsLoading}
        menuLoadingError={
          carModelsLoadingError || carModels.length === 0
            ? "عفوا لا يوجد موديلات لعرضها !"
            : ""
        }
        queryStringName={SKUS_QUERY_STRING.CAR_MODELS}
      />
      <SkusFilterDropdown
        label="سعة الموتور"
        dropdownText={getDropdownText(carTransmissionAndCCQueryString)}
        menu={carTransmissionsAndCCs}
        isDisabled={!carModelsQueryString}
        isMenuLoading={isCarTransmissionsAndCCsLoading}
        menuLoadingError={
          carTransmissionsAndCCsError || carTransmissionsAndCCs.length === 0
            ? "عفواً لا يوجد أختيارات لسعة الموتور !"
            : ""
        }
        menuItemClassName="numeric-content"
        searchClassName="numeric-content"
        queryStringName={SKUS_QUERY_STRING.CAR_TRANSMISSION_CC}
      />
      <SkusFilterDropdown
        label="سنة السيارة"
        dropdownText={getDropdownText(carYearListQueryString)}
        menu={carYearList}
        isDisabled={!carModelsQueryString}
        isMenuLoading={isCarYearListLoading}
        menuLoadingError={
          carYearListLoadingError || carYearList.length === 0
            ? "عفواً لا يوجد سنين لعرضها !"
            : ""
        }
        menuItemClassName="numeric-content"
        searchClassName="numeric-content"
        queryStringName={SKUS_QUERY_STRING.CAR_YEAR_LIST}
      />
    </ul>
  );
};

export default SkusFilters;

SkusFilters.propTypes = {
  className: PropTypes.string,
};
